import { getAdminUser } from "../api/adminClient";

export const ASSESSMENT_FORMAT_REQUIRED_MESSAGE =
  "This company has no assessment format assigned. Open Studio → Companies, choose Assessment format, save, then send invites.";

/** Tenant workspaces require a company-level assessment format before invites. */
export function isTenantInviteWorkspace(user = getAdminUser()) {
  if (!user) return false;
  if (user.is_super_admin && !user.tenant_id) return false;
  return !!(user.tenant_id || user.tenant_name);
}

export function companyHasAssessmentFormat(company) {
  if (!company || typeof company !== "object") return false;
  return !!(company.assessment_format_id || company.assessment_type_id);
}

export function findCompanyInList(companies, { companyId, companyName } = {}) {
  const list = Array.isArray(companies) ? companies : [];
  if (companyId) {
    const byId = list.find((c) => c.id === companyId);
    if (byId) return byId;
  }
  const nameKey = String(companyName || "").trim().toLowerCase();
  if (!nameKey) return null;
  return (
    list.find((c) => (c.company_name || "").trim().toLowerCase() === nameKey) || null
  );
}

/** @returns {string|null} User-facing error when invites must be blocked */
export function assessmentFormatRequiredError(
  companies,
  { companyId, companyName, user, workspace } = {}
) {
  if (!isTenantInviteWorkspace(user)) return null;
  const flatCo = flatWorkspaceCompany(workspace);
  const picked = findCompanyInList(companies, {
    companyId: flatCo?.id || companyId,
    companyName: flatCo?.company_name || companyName,
  });
  if (!picked) {
    return ASSESSMENT_FORMAT_REQUIRED_MESSAGE;
  }
  if (!companyHasAssessmentFormat(picked)) {
    return ASSESSMENT_FORMAT_REQUIRED_MESSAGE;
  }
  return null;
}

/** Participant URL from token only — never use stored survey_link from Mongo. */
export function participantAssessmentUrl(surveyToken) {
  const token = String(surveyToken || "").trim();
  if (!token) return "";
  const base = (typeof window !== "undefined" ? window.location.origin : "")
    .replace(/\/$/, "");
  return base ? `${base}/GetStartedPage/${token}` : "";
}

/** Classic 5X Super Admin fallback (no tenant workspace). */
export const CLASSIC_INVITE_ROLES = [
  "CEO",
  "Leadership Team",
  "Board Member",
  "PE Sponsor",
];

/** Generic tenant defaults when workspace.designations is empty. */
export const GENERIC_INVITE_ROLES = [
  "Executive",
  "Leadership Team",
  "Manager",
  "Contributor",
  "Other",
];

/** @deprecated Prefer rolesFromWorkspace() — kept for static imports. */
export const INVITE_ROLES = CLASSIC_INVITE_ROLES;

/**
 * Resolve invite role options from GET /workspace payload.
 * Tenant workspaces → designations labels (or generic list).
 * Classic (no workspace) → PE/5X titles.
 */
export function rolesFromWorkspace(workspace) {
  const des = workspace?.designations;
  if (Array.isArray(des) && des.length > 0) {
    return [...des]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((d) => String(d.label || "").trim())
      .filter(Boolean);
  }
  if (workspace?.id || workspace?.email_pack === "generic") {
    return [...GENERIC_INVITE_ROLES];
  }
  return [...CLASSIC_INVITE_ROLES];
}

export const INVITE_STATUSES = [
  "Not started",
  "Pending",
  "LiteCompleted",
  "Completed",
];

export const SAMPLE_CSV_CLASSIC =
  "First Name,Last Name,Email,Role\n" +
  "Emily,Davis,emily.davis@example.com,CEO\n" +
  "Mike,Thompson,Mike.thompson@example.com,Board Member\n" +
  "Liam,Wong,liam.wong@example.com,Leadership Team\n" +
  "Sofia,Martinez,sofia.martinez@example.com,PE Sponsor";

export const SAMPLE_CSV_GENERIC =
  "First Name,Last Name,Email,Role\n" +
  "Emily,Davis,emily.davis@example.com,Executive\n" +
  "Mike,Thompson,Mike.thompson@example.com,Manager\n" +
  "Liam,Wong,liam.wong@example.com,Leadership Team\n" +
  "Sofia,Martinez,sofia.martinez@example.com,Contributor";

/** @deprecated Prefer sampleCsvForWorkspace() */
export const SAMPLE_CSV = SAMPLE_CSV_CLASSIC;

export function sampleCsvForWorkspace(workspace) {
  if (workspace?.id || workspace?.email_pack === "generic") {
    return SAMPLE_CSV_GENERIC;
  }
  return SAMPLE_CSV_CLASSIC;
}

export const WORKSPACE_COMPANY_MODEL_FLAT = "flat";
export const WORKSPACE_COMPANY_MODEL_PORTFOLIO = "portfolio";

/** True when the tenant uses one implicit organization (no company picker in invites). */
export function isFlatWorkspace(workspace) {
  return workspace?.company_model === WORKSPACE_COMPANY_MODEL_FLAT;
}

/** Resolved company for flat-workspace invite sends. */
export function flatWorkspaceCompany(workspace) {
  if (!isFlatWorkspace(workspace)) return null;
  const dc = workspace?.default_company;
  if (!dc?.company_name) return null;
  return {
    id: dc.id || "",
    company_name: dc.company_name,
    company_logo: dc.company_logo || "",
  };
}

/** LITE → full path and LITE report offer (classic 5X workspaces only). */
export function workspaceLiteInviteAvailable(workspace) {
  if (workspace?.lite_invite_available === false) return false;
  if (workspace?.lite_invite_available === true) return true;
  const ps = workspace?.product_settings;
  if (ps?.lite_policy === "disabled") return false;
  if (ps?.lite_policy === "enabled") return true;
  if (!workspace?.id) return true;
  return workspace.email_pack === "5x";
}

/** Apply workspace-configured defaults to Engagement compose state. */
export function workspaceInviteDefaults(workspace) {
  if (!workspaceLiteInviteAvailable(workspace)) {
    return { assessment_mode: "full_only", offer_lite_report: "N" };
  }
  return { assessment_mode: "lite_to_full", offer_lite_report: "Y" };
}

export function inviteStatusClass(status) {
  const s = status || "Not started";
  if (s === "Completed") return "invite-status invite-status--completed";
  if (s === "LiteCompleted") return "invite-status invite-status--lite-completed";
  if (s === "Pending") return "invite-status invite-status--pending";
  return "invite-status invite-status--not-started";
}

export function formatInviteStatus(status) {
  if (status === "LiteCompleted") return "Lite completed";
  return status || "Not started";
}

/** Map NEXUS pipeline links onto invite-list filter ids. */
export function normalizePipelineStatus(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (!s || s === "all") return "all";
  if (["unfinished", "not finished", "stuck", "incomplete"].includes(s)) return "unfinished";
  if (["not started", "notstarted"].includes(s)) return "Not started";
  if (s === "in progress") return "in_progress";
  if (["pending"].includes(s)) return "Pending";
  if (["litecompleted", "lite completed", "lite"].includes(s)) return "LiteCompleted";
  if (["completed", "full completed", "full"].includes(s)) return "Completed";
  return "all";
}
